import { Badge } from "../../../../components/ui/common/badge";
import { TableCell, TableRow } from "../../../../components/ui/common/table";
import { DISABLE_COMPANY_LIST_CREATE_ON_COLUMN } from "../../../../config/constants";
import { CompanyRow } from "../types/companyListView.types";
import CompanyAllowCountButton from "./CompanyAllowCountButton";
import CompanyRowActions from "./CompanyRowActions";
import { companyTableColumnClass } from "./companyTable.columns";

/** The built-in admin tenant: legacy hid its view/edit/delete icons, so this does too. */
const PROTECTED_COMPANY_NAME = "NP Admin Company";

interface CompanyTableRowProps {
  row: CompanyRow;
  onView: (row: CompanyRow) => void;
  onEdit: (row: CompanyRow) => void;
  onDelete: (row: CompanyRow) => void;
  onAddRecipeIngredients: (row: CompanyRow) => void;
  onAddGenericProcess: (row: CompanyRow) => void;
}

/** Date-only "YYYY-MM-DD" (UTC); "—" for a missing/invalid date instead of throwing. */
const formatCreatedAt = (value?: string) => {
  const date = value ? new Date(value) : null;
  if (!date || isNaN(date.getTime())) return "—";
  return date.toISOString().slice(0, 10);
};

const formatStatus = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

/** One company's grid row. */
const CompanyTableRow = ({
  row,
  onView,
  onEdit,
  onDelete,
  onAddRecipeIngredients,
  onAddGenericProcess
}: CompanyTableRowProps) => {
  const isProtected = row.name === PROTECTED_COMPANY_NAME;

  return (
    <TableRow className="tw-bg-white hover:tw-bg-muted/50">
      <TableCell className={companyTableColumnClass.name}>
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-min-w-0">
            <div className="tw-text-sm tw-font-medium tw-text-foreground">{row.name}</div>
            <div className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground lg:tw-hidden">
              <span className="tw-break-all">{row.companyEmail || "—"}</span>
              {!DISABLE_COMPANY_LIST_CREATE_ON_COLUMN ? (
                <>
                  <span>&middot;</span>
                  <span className="tw-font-mono">{formatCreatedAt(row.createdAt)}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className={`${companyTableColumnClass.email} tw-text-sm tw-text-foreground`}>
        {row.companyEmail || "—"}
      </TableCell>
      <TableCell className={companyTableColumnClass.status}>
        {row.status ? (
          <Badge variant="outline" className="tw-rounded-full tw-text-[11px] tw-font-normal">
            {formatStatus(row.status)}
          </Badge>
        ) : (
          "—"
        )}
      </TableCell>
      {!DISABLE_COMPANY_LIST_CREATE_ON_COLUMN ? (
        <>
          <TableCell
            className={`${companyTableColumnClass.createdOn} tw-font-mono tw-text-sm tw-text-foreground`}
          >
            {formatCreatedAt(row.createdAt)}
          </TableCell>
          <TableCell className={companyTableColumnClass.recipeIngredients}>
            <CompanyAllowCountButton
              kind="ingredients"
              tenantId={row.tenantId}
              onClick={() => onAddRecipeIngredients(row)}
            />
          </TableCell>
          <TableCell className={companyTableColumnClass.genericProcesses}>
            <CompanyAllowCountButton
              kind="processes"
              tenantId={row.tenantId}
              onClick={() => onAddGenericProcess(row)}
            />
          </TableCell>
        </>
      ) : null}
      <TableCell className={companyTableColumnClass.actions}>
        {!isProtected ? (
          <CompanyRowActions row={row} onView={onView} onEdit={onEdit} onDelete={onDelete} />
        ) : null}
      </TableCell>
    </TableRow>
  );
};

export default CompanyTableRow;
